/*
 * TPS — hover intent for the header mega menu.
 *
 * Problem this solves: Horizon's HeaderMenu opens a panel on the first pointerenter
 * and closes it on the first pointerleave, with no grace either side. The panel itself
 * animates over 360ms (--submenu-animation-speed), so dragging the cursor across the
 * nav starts and reverses several animations in a row, and a one-frame dip outside the
 * safety box on the way down into a panel snaps it shut. That is the sloppiness.
 *
 * Fix, applied per instance rather than by forking header-menu.js:
 *   - opening waits for the pointer to settle, so skimming the nav opens nothing;
 *   - switching between items while a panel is already down stays instant;
 *   - closing gets a grace period, cancelled by any re-entry;
 *   - keyboard focus is never delayed, in either direction.
 *
 * `activate` is an instance field and `deactivate` a prototype method; Horizon resolves
 * both from the element at dispatch time, so shadowing them on the instance is enough.
 */

const OPEN_INTENT_DELAY_MS = 110;
const CLOSE_GRACE_MS = 240;

function patch(el) {
  if (!el || el.__tpsHoverIntent) return;
  el.__tpsHoverIntent = true;

  const originalActivate = el.activate;
  const prototypeDeactivate = Object.getPrototypeOf(el).deactivate;
  if (typeof originalActivate !== 'function' || typeof prototypeDeactivate !== 'function') return;

  const runActivate = (event) => originalActivate.call(el, event);
  const runDeactivate = (event) => prototypeDeactivate.call(el, event);

  let openTimer;
  let closeTimer;

  const panelIsOpen = () => el.getAttribute('aria-expanded') === 'true';

  el.activate = (event) => {
    clearTimeout(closeTimer);
    closeTimer = undefined;

    if (event instanceof FocusEvent) {
      clearTimeout(openTimer);
      openTimer = undefined;
      runActivate(event);
      return;
    }

    clearTimeout(openTimer);

    if (panelIsOpen()) {
      openTimer = undefined;
      runActivate(event);
      return;
    }

    openTimer = setTimeout(() => {
      openTimer = undefined;
      runActivate(event);
    }, OPEN_INTENT_DELAY_MS);
  };

  el.deactivate = (event) => {
    clearTimeout(openTimer);
    openTimer = undefined;

    if (event instanceof FocusEvent) {
      clearTimeout(closeTimer);
      closeTimer = undefined;
      runDeactivate(event);
      return;
    }

    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      closeTimer = undefined;
      runDeactivate(event);
    }, CLOSE_GRACE_MS);
  };
}

function patchAll() {
  document.querySelectorAll('header-menu').forEach(patch);
}

customElements.whenDefined('header-menu').then(() => {
  patchAll();
  // The header re-renders on section updates and on theme-editor edits.
  new MutationObserver(patchAll).observe(document.documentElement, { childList: true, subtree: true });
});
